import { format } from "date-fns";
import { Pencil, Radar } from "lucide-react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import type { LifeWheel } from "../types";
import { LifeWheelDialog } from "./LifeWheelDialog";
import { LifeWheelRadar } from "./LifeWheelRadar";
import { MIN_CHART_TOPICS, getChartTopics } from "./lifeWheelTopics";
import { useDateLocale } from "./useDateLocale";

export const LifeWheelCard = () => {
  const translate = useTranslate();
  const { dateFnsLocale } = useDateLocale();
  const { identity } = useGetIdentity();
  const [isDialogOpen, setDialogOpen] = useState(false);

  const { data, isPending } = useGetList<LifeWheel>(
    "life_wheel",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "updated_at", order: "DESC" },
      filter: { sales_id: identity?.id },
    },
    { enabled: identity?.id != null },
  );
  const lifeWheel = data?.[0] ?? null;
  const chartTopics = getChartTopics(lifeWheel?.scores);

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-semibold text-muted-foreground">
              <Radar className="size-5" />
              {translate("crm.personal.life_wheel.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {translate("crm.personal.life_wheel.subtitle")}
              {lifeWheel?.updated_at &&
                ` · ${translate("crm.personal.life_wheel.updated_at", {
                  date: format(new Date(lifeWheel.updated_at), "PPP", {
                    locale: dateFnsLocale,
                  }),
                })}`}
            </p>
          </div>
          {lifeWheel && (
            <Button variant="outline" onClick={() => setDialogOpen(true)}>
              <Pencil />
              {translate("crm.personal.life_wheel.edit")}
            </Button>
          )}
        </div>

        {isPending ? (
          <Skeleton className="mx-auto aspect-square w-full max-w-[420px]" />
        ) : !lifeWheel ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="max-w-md text-sm text-muted-foreground">
              {translate("crm.personal.life_wheel.empty")}
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              {translate("crm.personal.life_wheel.fill")}
            </Button>
          </div>
        ) : chartTopics.length < MIN_CHART_TOPICS ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {translate("crm.personal.life_wheel.min_topics")}
          </p>
        ) : (
          <LifeWheelRadar topics={chartTopics} />
        )}
      </CardContent>

      <LifeWheelDialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        lifeWheel={lifeWheel}
      />
    </Card>
  );
};
